import React, { useState } from 'react';
import type { PlatformUser } from '../../types/user';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Badge } from '../../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogFooter,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Shield } from 'lucide-react';
import { UserActionsMenu } from './UserActionsMenu';
import { updateBulkUser } from '../../../../redux/UserManagement/saga';

interface UserListProps {
  users: PlatformUser[];
  selectedUsers: string[];
  onSelectUser: (userId: string) => void;
  onSelectAll: () => void;
  onEditUser: (user: PlatformUser) => void;
  onDeleteUser: (user: PlatformUser) => void;
  onResetPassword: (user: PlatformUser) => void;
  onToggleStatus: (user: PlatformUser) => void;
  currentUser: PlatformUser;
  loading?: boolean;
  deleteUserLoading?: boolean;
  resetPasswordLoading?: boolean;
  toggleTwoFactorLoading?: boolean;
}

export const UserList: React.FC<UserListProps> = ({
  users,
  selectedUsers,
  onSelectUser,
  onSelectAll,
  onEditUser,
  onDeleteUser,
  onResetPassword,
  onToggleStatus,
  currentUser,
  loading = false,
  deleteUserLoading = false,
  resetPasswordLoading = false,
  toggleTwoFactorLoading = false
}) => {

  const allSelected = users.length > 0 && selectedUsers.length === users.length;

 
  const getStatusColor = (status: PlatformUser['status']) => {
    switch (status) {
      case 'active':
        return 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'pending':
        return 'text-[#B38600] bg-[rgba(230,161,0,0.08)]';
      case 'suspended':
        return 'text-readable-red bg-[rgba(196,0,31,0.06)]';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleBadgeColor = (role: PlatformUser['role']) => {
    switch (role) {
      case 'super_admin':
        return 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]';
      case 'admin':
        return 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]';
      case 'manager':
        return 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]';
      case 'viewer':
        return 'bg-gray-100 text-gray-800';
      case 'user':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">Loading users...</div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  return (
    <Table containerClassName="relative w-full overflow-x-auto">
        <TableHeader>
          <TableRow>
            <TableHead>
              <Checkbox
                checked={allSelected}
                onCheckedChange={onSelectAll}
                aria-label="Select all users"
                disabled={loading}
              />
            </TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Department</TableHead>
            <TableHead>Company</TableHead>
            <TableHead>2FA</TableHead>
            <TableHead>Last Login</TableHead>
            <TableHead className="sticky right-0 z-20 bg-page-bg w-[1%] whitespace-nowrap text-right shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id} className={selectedUsers.includes(user.id) ? 'bg-gray-50' : undefined}>
              <TableCell>
                <Checkbox
                  checked={selectedUsers.includes(user.id)}
                  onCheckedChange={() => onSelectUser(user.id)}
                  aria-label={`Select ${user.name}`}
                  disabled={loading}
                />
              </TableCell>
              <TableCell>
                <div className="flex items-center">
                  <div className="text-sm font-medium text-gray-900">{user.name}</div>
                  {user.requirePasswordChange && (
                    <Badge variant="outline" className="ml-2 text-yellow-600 border-yellow-600">
                      Password Change Required
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm text-gray-500">{user.email}</div>
              </TableCell>
              <TableCell>
                <Badge className={getRoleBadgeColor(user.role)}>
                  {user.role === 'super_admin' ? 'Super Admin' :
                    user.role === 'admin' ? 'Admin' :
                      user.role === 'manager' ? 'Manager' :
                        user.role === 'viewer' ? 'Viewer' :
                          user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge className={getStatusColor(user.status)}>
                  {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="text-sm text-gray-500">{user.profile.department?.name || '-'}</div>
              </TableCell>
              <TableCell>
                <div className="text-sm text-gray-500">{user.profile.company?.name || '-'}</div>
              </TableCell>
              <TableCell>
                <div className="flex items-center">
                  {user.twoFactorEnabled ? (
                    <Shield className="h-4 w-4 text-green-500" />
                  ) : (
                    <Shield className="h-4 w-4 text-gray-300" />
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm text-gray-500">
                  {formatDate(user.lastLogin)}
                </div>
              </TableCell>
              <TableCell
                className={`sticky right-0 z-10 w-[1%] whitespace-nowrap text-right shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)] ${
                  selectedUsers.includes(user.id) ? 'bg-gray-50' : 'bg-white'
                }`}
              >
                <div className="flex items-center justify-end">
                  <UserActionsMenu
                    user={user}
                    currentUser={currentUser}
                    onEdit={onEditUser}
                    onResetPassword={onResetPassword}
                    onDisableUser={onToggleStatus}
                    onDelete={onDeleteUser}
                    deleteUserLoading={deleteUserLoading}
                    resetPasswordLoading={resetPasswordLoading}
                    toggleTwoFactorLoading={toggleTwoFactorLoading}
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
    </Table>
  );
}; 